import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Cpu, Bot, Radio, Waves, Bookmark, ShieldCheck, Menu, Cloud, KeyRound, ShieldAlert, Wrench, Siren, MonitorPlay, LifeBuoy, Gauge, LayoutPanelLeft, ClipboardList, Route, LayoutGrid, Rocket, Sparkles, Download, Settings as SettingsIcon, LogOut, Users, CreditCard, FileText, BarChart3, ChevronDown, PanelLeftClose, PanelLeftOpen, ChevronRight, ArrowLeft } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { NotificationBell } from "./NotificationBell";
import { TrialBanner } from "./TrialBanner";
import { InstallPrompt } from "./InstallPrompt";
import { useAuth } from "../context/AuthContext";

const topLinks = [
  { to: "/", label: "Overview", icon: LayoutGrid, testId: "nav-overview", end: true },
  { to: "/start", label: "Get Started", icon: Sparkles, testId: "nav-start" },
];

const navGroups = [
  {
    id: "assess", label: "Assess", icon: LayoutDashboard, testId: "navgroup-assess",
    items: [
      { to: "/diagnostics", label: "Diagnostics", icon: LayoutDashboard, testId: "nav-diagnostics" },
      { to: "/site360", label: "Site 360", icon: LayoutPanelLeft, testId: "nav-site360" },
      { to: "/health-checks", label: "Health Checks", icon: ShieldCheck, testId: "nav-health-checks" },
      { to: "/firmware", label: "Firmware Lookup", icon: Cpu, testId: "nav-firmware" },
    ],
  },
  {
    id: "migrate", label: "Migrate", icon: Route, testId: "navgroup-migrate",
    items: [
      { to: "/migration", label: "Cloud Migration", icon: Cloud, testId: "nav-migration" },
      { to: "/runbook", label: "Migration Runbook", icon: Route, testId: "nav-runbook" },
      { to: "/scoreboard", label: "Estate Scoreboard", icon: LayoutGrid, testId: "nav-scoreboard" },
      { to: "/waves", label: "Wave Cutover", icon: Rocket, testId: "nav-waves" },
    ],
  },
  {
    id: "operate", label: "Operate & Compliance", icon: ShieldCheck, testId: "navgroup-operate",
    items: [
      { to: "/licenses", label: "Licenses", icon: KeyRound, testId: "nav-licenses" },
      { to: "/certificates", label: "Certificates", icon: ShieldAlert, testId: "nav-certificates" },
      { to: "/e911", label: "E911 Compliance", icon: Siren, testId: "nav-e911" },
      { to: "/dr", label: "DR & Failover", icon: LifeBuoy, testId: "nav-dr" },
      { to: "/qos", label: "QoS Builder", icon: Gauge, testId: "nav-qos" },
      { to: "/changes", label: "Change Log", icon: ClipboardList, testId: "nav-changes" },
      { to: "/rooms", label: "Room Collab", icon: MonitorPlay, testId: "nav-rooms" },
    ],
  },
  {
    id: "tools", label: "Tools & AI", icon: Wrench, testId: "navgroup-tools",
    items: [
      { to: "/tools", label: "Tools", icon: Wrench, testId: "nav-tools" },
      { to: "/assistant", label: "AI Assistant", icon: Bot, testId: "nav-assistant" },
    ],
  },
  {
    id: "reports", label: "Reports", icon: Bookmark, testId: "navgroup-reports",
    items: [
      { to: "/reports", label: "Reports", icon: Bookmark, testId: "nav-reports" },
      { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
      { to: "/capabilities", label: "White Paper", icon: FileText, testId: "nav-capabilities" },
    ],
  },
  {
    id: "workspace", label: "Workspace", icon: Users, testId: "navgroup-workspace",
    items: [
      { to: "/team", label: "Team", icon: Users, testId: "nav-team" },
      { to: "/billing", label: "Billing", icon: CreditCard, testId: "nav-billing" },
      { to: "/support", label: "Support", icon: LifeBuoy, testId: "nav-support" },
      { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
    ],
  },
];

const Brand = ({ onClick, collapsed }) => (
  <button onClick={onClick} data-testid="logo-home-btn" className={`flex items-center gap-2.5 text-left ${collapsed ? "justify-center w-full" : ""}`}>
    <img src="/logo-mark.png" alt="UC Engineer Pro" className="h-9 w-9 rounded-lg object-cover shrink-0 ring-1 ring-blue-500/30" />
    {!collapsed && (
      <div className="leading-none">
        <div className="font-heading font-extrabold text-[15px] tracking-tight text-white">UC Engineer Pro</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">UC Engineering Workspace</div>
      </div>
    )}
  </button>
);

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
    isActive
      ? "bg-blue-600/15 text-blue-400 border border-blue-600/30"
      : "text-zinc-400 hover:text-white hover:bg-zinc-800/60 border border-transparent"
  }`;

const subLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${
    isActive
      ? "bg-blue-600/15 text-blue-400 border border-blue-600/30"
      : "text-zinc-400 hover:text-white hover:bg-zinc-800/60 border border-transparent"
  }`;

const NavLinks = ({ onNavigate, collapsed = false, onExpand }) => {
  const location = useLocation();
  const activeGroupId =
    navGroups.find((g) =>
      g.items.some((it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"))
    )?.id || null;
  const [openId, setOpenId] = useState(activeGroupId);

  // Accordion resets to the active group on each navigation / reload.
  useEffect(() => { setOpenId(activeGroupId); }, [activeGroupId]);

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  // Collapsed (icon-only) rail: top links + group icons only; clicking a group
  // re-opens the sidebar and expands that group.
  if (collapsed) {
    return (
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {topLinks.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            onClick={onNavigate}
            title={item.label}
            className={({ isActive }) =>
              `flex items-center justify-center h-10 rounded-md transition-colors ${
                isActive ? "bg-blue-600/15 text-blue-400 border border-blue-600/30" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60 border border-transparent"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
          </NavLink>
        ))}
        {navGroups.map((group) => {
          const isActiveGroup = group.id === activeGroupId;
          return (
            <button
              key={group.id}
              type="button"
              title={group.label}
              data-testid={`${group.testId}-collapsed`}
              onClick={() => { onExpand && onExpand(); setOpenId(group.id); }}
              className={`w-full flex items-center justify-center h-10 rounded-md transition-colors border ${
                isActiveGroup ? "bg-blue-600/15 text-blue-400 border-blue-600/30" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60 border-transparent"
              }`}
            >
              <group.icon className="h-5 w-5" />
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className="flex-1 overflow-y-auto p-3 space-y-1">
      {topLinks.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testId}
          onClick={onNavigate}
          className={linkClass}
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}

      {navGroups.map((group) => {
        const isOpen = openId === group.id;
        return (
          <div key={group.id}>
            <button
              type="button"
              onClick={() => toggle(group.id)}
              data-testid={group.testId}
              aria-expanded={isOpen}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium transition-colors border border-transparent ${
                isOpen ? "text-white bg-zinc-800/40" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
              }`}
            >
              <span className="flex items-center gap-3">
                <group.icon className="h-4 w-4" />
                {group.label}
              </span>
              <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="mt-1 mb-1 ml-4 pl-3 border-l border-zinc-800 space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    onClick={onNavigate}
                    className={subLinkClass}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const AccountBox = ({ collapsed = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const plan = user.workspace?.plan;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  const doLogout = async () => { await logout(); navigate("/login"); };

  if (collapsed) {
    return (
      <div className="px-2 pt-3 pb-1 border-t border-zinc-800 flex flex-col items-center gap-2" data-testid="account-box">
        {user.picture ? (
          <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" title={user.name || user.email} />
        ) : (
          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold" title={user.name || user.email}>{initial}</div>
        )}
        <button onClick={doLogout} data-testid="logout-btn" title="Sign out" className="h-8 w-8 flex items-center justify-center rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 border-t border-zinc-800" data-testid="account-box">
      <div className="flex items-center gap-2.5 px-2 py-2">
        {user.picture ? (
          <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">{initial}</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-white truncate" data-testid="account-name">{user.name || user.email}</div>
          <div className="text-[10px] text-zinc-500 truncate">{user.workspace?.name || "Workspace"}</div>
        </div>
      </div>
      <div className="flex items-center justify-between px-2 pb-1">
        {plan && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${plan === "pro" ? "bg-emerald-600/20 text-emerald-400 border border-emerald-600/30" : "bg-blue-600/20 text-blue-400 border border-blue-600/30"}`} data-testid="plan-badge">
            {plan === "pro" ? "Pro" : "Free"}
          </span>
        )}
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-white transition-colors">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
};

const CollapseToggle = ({ collapsed, onToggle }) => (
  <div className={`border-t border-zinc-800 ${collapsed ? "p-2" : "p-3"}`}>
    <button
      type="button"
      onClick={onToggle}
      data-testid="sidebar-collapse-toggle"
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className={`flex items-center gap-2 rounded-md text-[12px] font-medium text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors ${
        collapsed ? "w-full justify-center h-10" : "w-full px-3 py-2"
      }`}
    >
      {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
    </button>
  </div>
);

const StatusFooter = ({ collapsed = false }) => {
  if (collapsed) {
    return (
      <div className="p-3 border-t border-zinc-800 flex justify-center" title="Knowledge base online">
        <span className="cursor-blink text-emerald-500 text-xs">●</span>
      </div>
    );
  }
  return (
    <div className="p-4 border-t border-zinc-800">
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <Radio className="h-3.5 w-3.5 text-emerald-500" />
        <span className="cursor-blink text-emerald-500">●</span>
        Knowledge base online
      </div>
    </div>
  );
};

const BackBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  if (path === "/") return null;

  let group = null;
  let page = null;
  for (const g of navGroups) {
    const it = g.items.find((i) => path === i.to || path.startsWith(i.to + "/"));
    if (it) { group = g; page = it; break; }
  }
  const top = topLinks.find((i) => path === i.to);
  const pageLabel = page?.label || top?.label || "";

  const goBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="no-print flex items-center gap-2.5 px-5 sm:px-8 h-11 border-b border-zinc-800 bg-zinc-950/40 text-[12px]">
      <button onClick={goBack} data-testid="back-btn" className="flex items-center gap-1.5 text-zinc-300 hover:text-white font-medium transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <span className="text-zinc-700">·</span>
      <button onClick={() => navigate("/")} data-testid="crumb-home" className="text-zinc-500 hover:text-white transition-colors">Home</button>
      {group && (
        <>
          <ChevronRight className="h-3.5 w-3.5 text-zinc-600 shrink-0" />
          <button onClick={() => navigate(group.items[0].to)} data-testid="crumb-group" className="text-zinc-500 hover:text-white transition-colors truncate">{group.label}</button>
        </>
      )}
      {pageLabel && (
        <>
          <ChevronRight className="h-3.5 w-3.5 text-zinc-600 shrink-0" />
          <span className="text-zinc-300 font-medium truncate" data-testid="crumb-current">{pageLabel}</span>
        </>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ucmp_sidebar_collapsed") === "1");

  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem("ucmp_sidebar_collapsed", next ? "1" : "0");
    return next;
  });
  const expandSidebar = () => { setCollapsed(false); localStorage.setItem("ucmp_sidebar_collapsed", "0"); };

  useEffect(() => {
    import("../lib/api").then((m) => m.getSettings().then(setBrand).catch(() => {}));
  }, []);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // First-run: send new users to the Get Started kickoff once.
  useEffect(() => {
    if (localStorage.getItem("lvai_onboarded") !== "1" && location.pathname === "/") {
      navigate("/start");
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${collapsed ? "w-16" : "w-60"} shrink-0 border-r border-zinc-800 bg-zinc-950 flex-col fixed h-screen z-30 transition-[width] duration-200`} data-testid="desktop-sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={`flex items-center h-16 border-b border-zinc-800 ${collapsed ? "justify-center px-0" : "justify-between px-5"}`}>
          <Brand onClick={() => navigate("/")} collapsed={collapsed} />
          {!collapsed && <NotificationBell />}
        </div>
        <NavLinks collapsed={collapsed} onExpand={expandSidebar} />
        <AccountBox collapsed={collapsed} />
        <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
        <StatusFooter collapsed={collapsed} />
      </aside>

      {/* Mobile top bar */}
      <div className="mobile-topbar lg:hidden sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="h-14 flex items-center justify-between px-4">
          <Brand onClick={() => navigate("/")} />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="h-10 w-10 flex items-center justify-center rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-800 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-zinc-950 border-zinc-800 flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Primary navigation menu</SheetDescription>
          <div className="flex items-center px-5 h-16 border-b border-zinc-800 shrink-0">
            <Brand onClick={() => { setOpen(false); navigate("/"); }} />
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col">
            <NavLinks onNavigate={() => setOpen(false)} />
            <AccountBox />
            <StatusFooter />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <main className={`${collapsed ? "lg:ml-16" : "lg:ml-60"} min-h-screen transition-[margin] duration-200`}>
        <TrialBanner />
        <InstallPrompt />
        <BackBar />
        {/* Print-only report letterhead */}
        <div className="print-show px-8 pt-6 pb-3 mb-2 border-b border-zinc-800 flex items-center gap-3">
          {brand?.logo && <img src={brand.logo} alt="logo" className="h-9 w-9 rounded object-contain" />}
          <div>
            <div className="font-heading font-extrabold text-lg text-white">{brand?.company_name || "UC Engineer Pro"}</div>
            <div className="text-[11px] text-zinc-500 font-mono">{brand?.tagline || "Field Diagnostics Report"} · generated {new Date().toLocaleString()}</div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
};
